<template>
    <div class="wrapper">
        <CoverSkeleton v-if="loading"/>
        <Cover v-else
            :currentPost="currentPost"
            :currentUser="currentPost.author"
            :isEditing="isEditing"
            :id="id"/>
            
        <NarrowContent>
            <div class="post">
                <article class="postContent">
                    <Contributor class="author"
                        v-if="!loading"
                        :slug="currentPost.author.slug"
                        :avatar="currentPost.author.avatar"
                        :name="currentPost.author.name"
                        :uploadTime="currentPost.uploadTime">
                        {{ siteContent.publishedBy }}
                    </Contributor>
                    <Feedback class="translationMessage"
                        v-if="!loading && !currentPost.deltaHu.length && preferences.language === 'hu'"
                        type="info"
                        icon="info">
                        <template slot="body">
                            <p>{{ siteContent.notTranslatedToHungarian }}</p>
                        </template>
                    </Feedback>
                    <section class="summary" v-if="!loading && !isEditing">
                    <Heading
                        v-if="!loading"
                        size="xxl">
                        {{ title }}
                    </Heading>
                        {{ summary }}
                    </section>
                    <section class="blogPost"
                        v-if="!isEditing">
                        <QuillContentSkeleton
                            v-if="loading"/>
                        <div class="post"
                            v-else
                            ref="formattedDelta"
                            v-html="formattedDelta">
                        </div>
                    </section>
                    <PostEditor
                        v-else
                        :currentUser="currentPost.author"
                        :currentPost="currentPost"
                        :id="id"
                        @finishedit="finishEdit"
                        @canceledit="cancelEdit"/>

                    <Button class="editButton"
                        v-if="!loading && !isEditing && hasPermissionToEdit"
                        @click="isEditing=true"
                        appearance="primary"
                        size="small"
                        prefixIcon="edit">
                        {{ siteContent.edit }}
                    </Button>

                    <BlurCapImage class="relatedEntry"
                        v-if="currentPost.relatedEntry && !entryLoading"
                        :routerLink="'/entry/' + currentArtwork.id"
                        :image="currentArtwork.media.carousel[0].url"
                        :badgeLabel="siteContent.relatedEntry"
                        badgeType="related">
                            <EntryDetail
                                :entry="currentArtwork"/>
                    </BlurCapImage>
                    
                    <AboutTheAuthor v-if="!loading"
                        :authorSlug="currentPost.author.slug"
                        :currentUser="currentPost.author"/>
                    
                    <div class="commentsWrapper">
                        <Comments v-if="!isEditing && !loading"
                            collection="blogComments"
                            :documentId="id"
                            :notificationDetails="notificationDetails"/>
                    </div>
                </article>
                <SocialShare
                    v-if="!isEditing && !loading"
                    :id="id"
                    :title="title"
                    :caption="summary"/>
            </div>
        </NarrowContent>
    </div>
</template>

<script>
    import { mapActions, mapState } from 'vuex'
    
    import CoverSkeleton        from '@/components/blog/post/CoverSkeleton'
    import QuillContentSkeleton from '@/components/blog/post/QuillContentSkeleton'
    import NarrowContent        from '@/components/layout/NarrowContent'
    import Cover                from '@/components/blog/post/Cover'
    import Contributor          from '@/components/entry/Contributor'
    import PostEditor           from '@/components/blog/post/PostEditor'
    import EntryDetail          from '@/components/journal/main/EntryDetail'
    import AboutTheAuthor       from '@/components/blog/post/AboutTheAuthor'
    import Comments             from '@/components/comments/Comments'
    import SocialShare          from '@/components/blog/post/SocialShare'

    import 'quill/dist/quill.snow.css'
    import Quill from 'quill'
    import { quillEditor } from 'vue-quill-editor'

    export default {
        name: 'Blog',
        components: {
            CoverSkeleton,
            QuillContentSkeleton,
            NarrowContent,
            quillEditor,
            Cover,
            Contributor,
            PostEditor,
            EntryDetail,
            AboutTheAuthor,
            Comments,
            SocialShare
        },
        props: {
            id: String
        },
        data() {
            return {
                loading: true,
                entryLoading: true,
                isEditing: false,
                mounting: true,
            }
        },
        computed: {
            ...mapState('blog',   ['currentPost']),
            ...mapState('journal',['currentArtwork']),

            hasPermissionToEdit() {
                return this.currentPost.author.id === this.auth.id || this.permissions.isAdmin
                
            },
            enableEdit() {
                const queryString = window.location.search
                const urlParams = new URLSearchParams(queryString)
                if(urlParams.has('enableEdit')) {
                    return true
                }
            },
            title() {
                if(this.preferences.language === 'hu' && this.currentPost.title.hu) {
                    return this.currentPost.title.hu
                } else {
                    return this.currentPost.title.en
                }
            },
            summary() {
                if(this.preferences.language === 'hu' && this.currentPost.summary.hu) {
                    return this.currentPost.summary.hu
                } else {
                    return this.currentPost.summary.en
                }
            },
            formattedDelta() {
                let article = document.createElement('article')
                let quill = new Quill(article, {
                    readOnly: true
                })

                if(this.preferences.language === 'hu' && this.currentPost.deltaHu.length) {
                    quill.setContents(this.currentPost.deltaHu)
                } else {
                    quill.setContents(this.currentPost.deltaEn)
                }
                
                setTimeout(() => {
                    this.$refs.formattedDelta.innerHTML = ''
                    this.$refs.formattedDelta.appendChild(article)
                }, 0)
            },
            notificationDetails() {
                return {
                    createdBy: this.currentPost.author.id,
                    title:     this.currentPost.title
                }
            }
        },
        async mounted() {
            await this.fetchPostByID(this.id)

            if(this.currentPost.visibility === false && (!this.permissions.isAdmin || this.auth.id === this.currentPost.author)) {
                this.$router.push('/auth')
            }
            this.loading = false

            if(this.permissions.isAdmin && this.enableEdit) {
                this.isEditing = true
            }

            if(this.currentPost.relatedEntry) {
                await this.fetchEntryByID(this.currentPost.relatedEntry)
                this.entryLoading = false
            }
        },
        methods: {
            ...mapActions('blog',['fetchPostByID']),
            ...mapActions('journal',['fetchEntryByID']),

            finishEdit() {
                this.isEditing = false
                location.reload()
            },
            cancelEdit() {
                this.isEditing = false
            },
            copyShareLink() {
                try {
                    navigator.clipboard.writeText(this.shareUrl)
                    this.$toasted.show(this.siteContent.copiedToClipBoard)
                } catch {
                    this.$toasted.show(this.siteContent.actionError)
                }
            }
        },
        beforeRouteLeave (to, from , next) {
            if(this.isEditing) {
                const answer = window.confirm(this.siteContent.confirmPageLeave)
                if(answer) {
                    next()
                }
            } else {
                next()
            }
        },
        metaInfo() {
            if(this.preferences.language === 'hu' && !this.loading) {
                return {
                    title: `${this.currentPost.title.hu + ' | Creatillo'}`,
                    meta: [
                        { vmid: 'description', name: 'description', content: `${this.currentPost.summary.hu}` },
                        { vmid: 'keywords', name: 'keywords', content: 'digital painting, digital painting ideas, universe art, space art' }
                    ]
                }
            } else if(this.preferences.language !== 'hu' && !this.loading) {
                return {
                    title: `${this.currentPost.title.en + ' | Creatillo'}`,
                    meta: [
                        { vmid: 'description', name: 'description', content: `${this.currentPost.summary.en}` },
                        { vmid: 'keywords', name: 'keywords', content: 'digital painting, digital painting ideas, universe art, space art' }
                    ]
                }
            }
        }
    }
</script>

<style scoped lang="scss">
    @import '../scss/variables';

    .post {
        position: relative;
        display: flex;
    }

    .author {
        margin-bottom: 3rem;
    }

    .postContent {
        display: flex;
        flex-direction: column;
        width: 100%;
    }

    .h1 {
        margin: 0 0 1.5rem 0;
    }

    .summary {
        font-size: 1.75rem;
        margin: 0 0 2rem 0;
    }

    .relatedEntry {
        margin-top: 4rem;
    }

    .editButton {
        margin-top: 2rem;
    }

    .commentsWrapper {
        margin-top: 4rem;
        width: 100%;
    }

    .translationMessage {
        margin: 1.5rem 0 2rem 0;
    }

    @media all and (max-width: 880px) {
        .post {
            flex-direction: column;
        }
    }
</style>

<style lang="scss">
    @import '../scss/variables';
    @import '../scss/quill';
</style>